import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ChartComponent } from '../../../../shared/components/chart/chart.component';
import { DashboardKpis, DashboardSnapshot } from '../../models/dashboard.model';
import { FiscalOperationType, InvoiceStatus } from '../../../invoices/models/invoice.model';
import { delta, deltaClass, deltaIcon } from '../../utils/dashboard-format';
import { buildRevenueBarChart, channelSeries, revenueDays } from '../../utils/dashboard-charts';

// Faturamento por dia, em barras: um ponto por dia do período (dia sem nota aparece zerado) e, em
// "Todos os canais" com 2 ou mais canais, barras empilhadas por canal com legenda embaixo. O valor
// e a variação do cabeçalho vêm dos mesmos `kpis` do card Faturamento (nada de recalcular a
// variação por conta própria): período anterior nulo ou aviso de período curto = sem variação.
// Sem `revenueByDay` (API antiga), usa `revenueSeries` com os dias sem nota zerados aqui no front.
// Host = a própria coluna (col direta da .row → gutters e distribuição do dash-row funcionam).
@Component({
  selector: 'app-dashboard-revenue-chart',
  standalone: true,
  imports: [CommonModule, RouterModule, NgbTooltipModule, ChartComponent],
  host: { class: 'col-12 col-xl-8' },
  template: `
    <div class="card h-100" data-revenue-chart>
      <div class="card-header d-flex align-items-start justify-content-between gap-2">
        <!-- min-w-0 + flex-wrap: sem os dois, "R$ 68.733,68" e o delta na MESMA linha viram o
             min-content do bloco (flex item nasce min-width:auto) e o header vaza o card a 320px. -->
        <div class="min-w-0">
          <h5 class="card-title mb-1">Faturamento por dia</h5>
          <div class="d-flex align-items-baseline flex-wrap gap-2">
            <span class="stat-value" data-revenue-total>{{ revenue | currency: 'BRL' }}</span>
            @if (revenueDelta !== null) {
              <span class="stat-delta" [ngClass]="deltaClass(revenueDelta)">
                <i class="ti {{ deltaIcon(revenueDelta) }}"></i>
                {{ revenueDelta > 0 ? '+' : '' }}{{ revenueDelta }}%
              </span>
            }
          </div>
          @if (revenueDelta !== null) { <span class="stat-caption">vs. período anterior</span> }
        </div>
        <a class="btn btn-sm btn-icon btn-text-secondary rounded-circle" routerLink="/notas-fiscais"
          [queryParams]="revenueParams" ngbTooltip="Ver notas fiscais" container="body"
          aria-label="Ver notas fiscais">
          <i class="ti ti-arrow-up-right"></i>
        </a>
      </div>
      <div class="card-body pt-0">
        @if (chart) {
          <app-chart [options]="chart"></app-chart>
        }
      </div>
    </div>
  `,
})
export class DashboardRevenueChartComponent implements OnChanges {
  @Input() snapshot?: DashboardSnapshot;
  @Input() kpis?: DashboardKpis;
  // Período do dashboard (from/to yyyy-MM-dd) — deep-links abrem a lista no mesmo recorte do card.
  @Input() period?: { from?: string; to?: string };
  @Input() channelId?: number | null;
  /** Aviso de período curto: sem comparação com o período anterior (igual aos cards). */
  @Input() noComparison = false;
  readonly deltaClass = deltaClass;
  readonly deltaIcon = deltaIcon;

  chart: any;
  stacked = false;
  revenue = 0;
  revenueDelta: number | null = null;
  /**
   * Mesmo recorte do faturamento (período + autorizada + venda + canal). O total da lista bate com o
   * gráfico: as duas contam pela emissão (IssuedAt ?? CreatedAt) e a Autorizada exclui a cancelada.
   */
  revenueParams: Record<string, string | number | undefined> = {};

  ngOnChanges(): void {
    this.revenue = this.kpis?.revenue ?? this.snapshot?.invoices?.totalAmount ?? 0;
    this.revenueDelta = this.noComparison ? null : delta(this.revenue, this.kpis?.previousRevenue ?? 0);
    this.revenueParams = {
      ...this.period,
      ...(this.channelId != null ? { integrationId: this.channelId } : {}),
      status: InvoiceStatus.Authorized,
      fiscalOperation: FiscalOperationType.Sale,
      includeFulfilled: 'true',
      includeErp: 'true',
    };

    if (!this.snapshot) {
      this.chart = undefined;
      return;
    }
    const days = revenueDays(this.snapshot, this.period);
    const channels = this.snapshot.channels ?? [];
    // Empilha só com a quebra por canal da API nova, em "Todos os canais" e com 2+ canais.
    this.stacked = this.channelId == null && channels.length >= 2 && !!this.snapshot.revenueByDay;
    this.chart = buildRevenueBarChart(days, {
      stacked: this.stacked ? channelSeries(days, channels) : undefined,
      noDataText: 'Nenhum faturamento no período',
    });
  }
}
